/**
 * The engine ties the model, renderer, modules, input, and session together.
 *
 * One create module is active; affects stack over it. A dirty-flag rAF loop
 * redraws only when something changed. Pointer-up commits the live pipeline
 * output into the frozen scene and re-bakes the committed buffer.
 */
import type { RGBA, Scene, Shape } from './scene';
import { BLACK, cloneShape, defaultStyle, lerpRgba, makeScene } from './scene';
import type {
  AffectModule,
  CreateModule,
  InputSample,
  JSONValue,
  ModuleContext,
  ModuleManifest,
  PaletteAccess,
  Viewport,
} from './module-api';
import { paramDefaults } from './module-api';
import type { SceneRenderer } from '../render/renderer';
import { runPipeline } from './pipeline';
import { makeRng } from './rng';
import { Session } from './session';
import { AudioEngine } from '../audio/analyser';
import { uid } from '../util/id';

function neutralInput(): InputSample {
  return {
    x: 0,
    y: 0,
    pressure: 0.5,
    tiltX: 0,
    tiltY: 0,
    twist: 0,
    speed: 0,
    buttons: 0,
    pointerType: 'mouse',
    timeStamp: 0,
  };
}

export class Engine {
  readonly scene: Scene;
  onChange?: () => void;

  private renderer: SceneRenderer;
  private session = new Session();
  private audio = new AudioEngine();
  private create: CreateModule;
  private affects: AffectModule[] = [];
  private configs = new Map<string, Record<string, JSONValue>>();
  private viewport: Viewport;
  private input: InputSample = neutralInput();
  private guides: Shape[] = [];
  private dirty = true;
  private running = false;
  private seq = 0;
  private commitSeed = 1;
  private strokeInk: RGBA | null = null;
  private lastCommitIds = new Set<string>();
  private paletteTouched = false;
  private drawing = false;
  private blindRequested = false;
  private traceImage: string | null = null;
  private traceSeq = 0;

  constructor(renderer: SceneRenderer, create: CreateModule, width: number, height: number, dpr: number) {
    this.renderer = renderer;
    this.scene = makeScene(width, height);
    this.create = create;
    this.viewport = { width, height, dpr };
    this.registerConfig(create.manifest);
    this.renderer.resize(width, height, dpr);
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    create.setup?.(this.makeCtx(create.manifest.id));
  }

  // --- module config -------------------------------------------------------

  private registerConfig(manifest: ModuleManifest): void {
    if (!this.configs.has(manifest.id)) this.configs.set(manifest.id, paramDefaults(manifest));
  }

  /** Pre-register a module's defaults so setParam works before that module is selected. */
  ensureConfig(manifest: ModuleManifest): void {
    this.registerConfig(manifest);
  }

  setParam(moduleId: string, key: string, value: JSONValue): void {
    const cfg = this.configs.get(moduleId);
    if (!cfg) return;
    cfg[key] = value;
    this.markDirty();
  }

  getConfig(moduleId: string): Readonly<Record<string, JSONValue>> {
    return this.configs.get(moduleId) ?? {};
  }

  /** Shapes from the most recent commit (one "stroke"), for capture-to-set. */
  getLastCommitShapes(): Shape[] {
    return this.scene.shapes.filter((s) => this.lastCommitIds.has(s.id));
  }

  // --- palette / ink / background ------------------------------------------

  getPalette(): readonly RGBA[] {
    return this.scene.palette;
  }

  getActiveInk(): RGBA {
    const p = this.scene.palette;
    const i = Math.max(0, Math.min(this.scene.activeInk, p.length - 1));
    return { ...(p[i] ?? BLACK) };
  }

  getActiveInkIndex(): number {
    return Math.max(0, Math.min(this.scene.activeInk, this.scene.palette.length - 1));
  }

  getBackground(): RGBA {
    return { ...this.scene.background };
  }

  setActiveInk(i: number): void {
    this.scene.activeInk = Math.max(0, Math.min(i, this.scene.palette.length - 1));
    this.afterPaletteChange();
  }

  addSwatch(c: RGBA): void {
    this.scene.palette.push({ ...c });
    this.scene.activeInk = this.scene.palette.length - 1;
    this.afterPaletteChange();
  }

  editSwatch(i: number, c: RGBA): void {
    if (this.scene.palette[i]) {
      this.scene.palette[i] = { ...c };
      this.afterPaletteChange();
    }
  }

  removeSwatch(i: number): void {
    if (this.scene.palette.length <= 1) return;
    this.scene.palette.splice(i, 1);
    this.scene.activeInk = Math.max(0, Math.min(this.scene.activeInk, this.scene.palette.length - 1));
    this.afterPaletteChange();
  }

  setBackground(c: RGBA): void {
    this.paletteTouched = true;
    this.scene.background = { ...c };
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    this.session.save(this.scene);
    this.markDirty();
    this.onChange?.();
  }

  private afterPaletteChange(): void {
    this.paletteTouched = true;
    this.session.save(this.scene);
    this.markDirty();
    this.onChange?.();
  }

  private makePaletteAccess(): PaletteAccess {
    const p = this.scene.palette;
    const n = p.length;
    return {
      colors: p.map((c) => ({ ...c })), // defensive copy: never expose live swatch objects
      at: (i) => ({ ...(p[((i % n) + n) % n] ?? BLACK) }),
      sample: (rng) => ({ ...(p[Math.floor(rng.next() * n)] ?? BLACK) }),
      ramp: (t) => {
        if (n <= 1) return { ...(p[0] ?? BLACK) };
        const x = Math.max(0, Math.min(1, t)) * (n - 1);
        const i = Math.floor(x);
        return lerpRgba(p[i], p[Math.min(i + 1, n - 1)], x - i);
      },
    };
  }

  // --- context -------------------------------------------------------------

  private makeCtx(moduleId: string): ModuleContext {
    return {
      scene: this.scene,
      input: this.input,
      viewport: this.viewport,
      rng: makeRng(this.commitSeed),
      audio: this.audio.enabled ? this.audio.snapshot() : undefined,
      ink: this.strokeInk ?? this.getActiveInk(),
      palette: this.makePaletteAccess(),
      config: this.configs.get(moduleId) ?? {},
      newShape: (init) => this.newShape(init),
      drawGuide: (s) => this.guides.push(s),
      requestBlind: () => {
        this.blindRequested = true;
      },
    };
  }

  setTraceImage(url: string | null): void {
    this.traceImage = url;
    const token = ++this.traceSeq; // invalidate any in-flight decode (clear/replace race)
    if (!url) {
      this.renderer.setTraceImage(null);
      this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
      this.markDirty();
      this.onChange?.();
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (token !== this.traceSeq) return; // a newer load or a clear superseded this one
      this.renderer.setTraceImage(img);
      this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
      this.markDirty();
    };
    img.onerror = () => {
      if (token !== this.traceSeq) return;
      this.traceImage = null;
      this.renderer.setTraceImage(null);
      this.markDirty();
      this.onChange?.();
    };
    img.src = url;
    this.onChange?.();
  }

  getTraceImage(): string | null {
    return this.traceImage;
  }

  private newShape(init?: Partial<Shape>): Shape {
    return {
      id: init?.id ?? uid(),
      path: init?.path ?? { segments: [] },
      style: init?.style ?? defaultStyle(),
      variableWidth: init?.variableWidth,
      gradient: init?.gradient,
      meta: init?.meta ?? { module: this.create.manifest.id, seq: -1, t: Date.now() },
    };
  }

  private freeze(s: Shape): Shape {
    const c = cloneShape(s);
    c.id = uid();
    c.meta = { module: c.meta.module || this.create.manifest.id, seq: this.seq++, t: Date.now() };
    return c;
  }

  // --- module selection ----------------------------------------------------

  get activeCreate(): CreateModule {
    return this.create;
  }

  isAffectActive(m: AffectModule): boolean {
    return this.affects.includes(m);
  }

  setCreate(m: CreateModule): void {
    if (m === this.create) return;
    this.create.onDeselect?.();
    this.create = m;
    this.registerConfig(m.manifest);
    m.setup?.(this.makeCtx(m.manifest.id));
    m.onReselect?.();
    this.maybeEnableAudio(m.manifest);
    this.markDirty();
    this.onChange?.();
  }

  private maybeEnableAudio(manifest: ModuleManifest): void {
    if (manifest.capabilities?.includes('audio')) {
      void this.audio.enable().then(() => this.onChange?.());
    }
  }

  isAudioEnabled(): boolean {
    return this.audio.enabled;
  }

  /** Test hook: drive the mic snapshot directly without real audio. */
  debugSetAudio(volume: number, pitch: number | null): void {
    this.audio.debugSet(volume, pitch);
    this.markDirty();
  }

  toggleAffect(m: AffectModule): boolean {
    const i = this.affects.indexOf(m);
    if (i >= 0) {
      this.affects.splice(i, 1);
      m.onDeselect?.();
      this.markDirty();
      this.onChange?.();
      return false;
    }
    this.registerConfig(m.manifest);
    m.setup?.(this.makeCtx(m.manifest.id));
    this.affects.push(m);
    this.maybeEnableAudio(m.manifest);
    this.markDirty();
    this.onChange?.();
    return true;
  }

  // --- pointer flow --------------------------------------------------------

  pointerDown(s: InputSample): void {
    this.input = s;
    this.drawing = true;
    // Freeze the active ink for the whole stroke so changing the swatch
    // mid-stroke doesn't retroactively recolour the in-progress mark.
    this.strokeInk = this.getActiveInk();
    this.create.onPointerDown?.(s, this.makeCtx(this.create.manifest.id));
    this.markDirty();
  }

  pointerMove(s: InputSample): void {
    this.input = s;
    this.create.onPointerMove?.(s, this.makeCtx(this.create.manifest.id));
    this.markDirty();
  }

  pointerHover(s: InputSample): void {
    if (!this.create.onHover) return;
    this.input = s;
    this.create.onHover(s, this.makeCtx(this.create.manifest.id));
    this.markDirty();
  }

  pointerUp(s: InputSample): void {
    this.input = s;
    this.create.onPointerUp?.(s, this.makeCtx(this.create.manifest.id));
    this.commit();
  }

  // --- commit / history ----------------------------------------------------

  private commit(): void {
    const result = runPipeline(this.create, this.affects, (id) => this.makeCtx(id), true);
    const frozen = result.map((s) => this.freeze(s));
    this.create.onCommit?.(this.makeCtx(this.create.manifest.id));
    this.create.onClear?.();
    this.strokeInk = null;
    this.drawing = false;
    if (frozen.length === 0) {
      this.markDirty();
      return;
    }
    this.scene.shapes.push(...frozen);
    this.session.pushCommit(frozen.map((s) => s.id));
    this.lastCommitIds = new Set(frozen.map((s) => s.id));
    this.commitSeed++;
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    this.session.save(this.scene);
    this.markDirty();
    this.onChange?.();
  }

  undo(): void {
    const op = this.session.undo();
    if (!op) return;
    if (op.type === 'commit') {
      const ids = new Set(op.ids);
      this.scene.shapes = this.scene.shapes.filter((s) => !ids.has(s.id));
    } else {
      this.scene.shapes = op.shapes.map(cloneShape);
    }
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    this.session.save(this.scene);
    this.markDirty();
    this.onChange?.();
  }

  clear(): void {
    if (this.scene.shapes.length === 0) return;
    this.session.pushClear(this.scene.shapes.map(cloneShape));
    this.scene.shapes = [];
    this.create.onClear?.();
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    this.session.save(this.scene);
    this.markDirty();
    this.onChange?.();
  }

  canUndo(): boolean {
    return this.session.canUndo();
  }

  // --- lifecycle -----------------------------------------------------------

  resize(width: number, height: number, dpr: number): void {
    this.viewport = { width, height, dpr };
    this.scene.width = width;
    this.scene.height = height;
    this.renderer.resize(width, height, dpr);
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    this.markDirty();
  }

  async restore(): Promise<void> {
    const saved = await this.session.load();
    if (!saved) return;
    // Don't overwrite palette/ink/background the user changed during the async load.
    if (!this.paletteTouched) {
      if (Array.isArray(saved.palette) && saved.palette.length) {
        this.scene.palette = saved.palette.map((c) => ({ ...c }));
      }
      if (Number.isInteger(saved.activeInk)) {
        this.scene.activeInk = Math.max(0, Math.min(saved.activeInk, this.scene.palette.length - 1));
      }
      if (saved.background) this.scene.background = { ...saved.background };
    }
    // Don't clobber a stroke the user may have drawn during the async load.
    if (this.scene.shapes.length === 0 && saved.shapes.length) {
      this.scene.shapes = saved.shapes.map(cloneShape);
      this.seq = this.scene.shapes.length;
    }
    this.renderer.bakeCommitted(this.scene.shapes, this.scene.background);
    this.markDirty();
    this.onChange?.();
  }

  markDirty(): void {
    this.dirty = true;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
  }

  private frame = (): void => {
    if (this.running) requestAnimationFrame(this.frame);
    if (this.audio.enabled) this.audio.update();
    // While the mic is live, redraw every frame so audio-driven modules animate.
    if (!this.dirty && !this.audio.enabled) return;
    this.dirty = false;
    this.guides = [];
    this.blindRequested = false;
    const live = runPipeline(this.create, this.affects, (id) => this.makeCtx(id), false);
    for (const a of this.affects) a.drawGuides?.(this.makeCtx(a.manifest.id));
    if (this.blindRequested && this.drawing) this.renderer.renderBlank(this.scene.background);
    else this.renderer.renderFrame(live, this.guides);
  };
}
